import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  persistLiveTrainingSession,
  resolveLiveTrainingRedirect,
} from "./useLiveTrainingRedirect";
import {
  ensureCoachRoleForLiveTrainingEntry,
  isCoachLiveTrainingAutoEnterSuppressed,
} from "../utils/liveTrainingNav";
import { requestLiveFullscreen } from "./useLiveFullscreen";
import { isMobileDevice } from "../utils/isMobileDevice";
import { primeEvalCameraPermission } from "../utils/evalCameraPermission";

/**
 * When a coach lands on Home (or another surface) within 30 minutes of their
 * session, send them straight into Live Training — no manual "Live Training" CTA.
 * Skipped if they intentionally left Live Training for Home.
 */
export function useCoachLiveTrainingAutoEnter({
  enabled = false,
  user = null,
  permissions = [],
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !user?.user_id) return undefined;
    if (location.pathname === "/live-training") return undefined;
    if (attemptedRef.current) return undefined;

    let cancelled = false;
    attemptedRef.current = true;

    (async () => {
      try {
        const session = JSON.parse(localStorage.getItem("rsessn") || "null");
        if (session?.ts_id && isCoachLiveTrainingAutoEnterSuppressed(session.ts_id)) {
          return;
        }
        const path = await resolveLiveTrainingRedirect(user, permissions);
        if (cancelled || !path) {
          // Allow a later remount / role switch to try again if nothing matched yet.
          if (!path) attemptedRef.current = false;
          return;
        }
        const latest = JSON.parse(localStorage.getItem("rsessn") || "null");
        if (latest?.ts_id && isCoachLiveTrainingAutoEnterSuppressed(latest.ts_id)) {
          return;
        }
        ensureCoachRoleForLiveTrainingEntry(user, permissions);
        const grp = JSON.parse(localStorage.getItem("grpinf") || "null");
        if (latest?.ts_id) {
          persistLiveTrainingSession(latest, grp);
        }
        if (isMobileDevice()) {
          try {
            await primeEvalCameraPermission();
          } catch {
            /* ignore */
          }
          requestLiveFullscreen();
        }
        navigate(path, { replace: true });
      } catch (err) {
        attemptedRef.current = false;
        console.warn("Coach live-training auto-enter skipped:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, user, permissions, location.pathname, navigate]);
}

export default useCoachLiveTrainingAutoEnter;
